import React, { useState, useEffect, useRef } from 'react';
import {
    Container, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
    List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Checkbox,
    FormGroup, FormControlLabel, Typography, Paper, Box, Alert, CircularProgress,
    Accordion, AccordionSummary, AccordionDetails, Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchUserGroups, addUserGroup, updateUserGroup, deleteUserGroup } from '../../utils/api';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS, ACTIONS } from '../../utils/permissions';

// Helper function to get default privileges structure
const getDefaultPrivileges = () => ({
    tabVisibility: {
        home: true,
        appUsers: false,
        schemes: false,
        systemUsers: false,
        userGroups: false,
        masters: false,
        reports: false,
        auditLogs: false
    },
    appUsers: {
        create: false,
        import: false,
        export: false,
        read: false,
        write: false
    },
    appUserDetails: {
        username: false,
        email: false,
        phoneNo: false,
        dob: false
    },
    schemes: {
        import: false,
        export: false,
        read: false,
        write: false
    },
    systemUsers: {
        create: false,
        read: false,
        write: false
    },
    masters: {
        create: false,
        read: false,
        write: false
    },
    reports: {
        read: false,
        export: false
    }
});

// Migrate old privileges structure to new structure
const migratePrivilegesStructure = (oldPrivileges) => {
    const defaultPrivs = getDefaultPrivileges();
    
    // If already in new format, merge with defaults to ensure all fields exist
    if (oldPrivileges.tabVisibility) {
        return {
            tabVisibility: { ...defaultPrivs.tabVisibility, ...(oldPrivileges.tabVisibility || {}) },
            appUsers: { ...defaultPrivs.appUsers, ...(oldPrivileges.appUsers || {}) },
            appUserDetails: { ...defaultPrivs.appUserDetails, ...(oldPrivileges.appUserDetails || {}) },
            schemes: { ...defaultPrivs.schemes, ...(oldPrivileges.schemes || {}) },
            systemUsers: { ...defaultPrivs.systemUsers, ...(oldPrivileges.systemUsers || {}) },
            masters: { ...defaultPrivs.masters, ...(oldPrivileges.masters || {}) },
            reports: { ...defaultPrivs.reports, ...(oldPrivileges.reports || {}) }
        };
    }
    
    // Migrate from old structure
    const migrated = { ...defaultPrivs };
    
    // Migrate userPermissions to appUsers
    if (oldPrivileges.userPermissions) {
        migrated.appUsers = { ...defaultPrivs.appUsers, ...oldPrivileges.userPermissions };
        // Set tab visibility based on permissions
        if (oldPrivileges.userPermissions.read || oldPrivileges.userPermissions.write) {
            migrated.tabVisibility.appUsers = true;
        }
    }
    
    // Migrate userDetailsPermissions to appUserDetails
    if (oldPrivileges.userDetailsPermissions) {
        migrated.appUserDetails = { ...defaultPrivs.appUserDetails, ...oldPrivileges.userDetailsPermissions };
    }
    
    // Migrate yojnaPermissions to schemes
    if (oldPrivileges.yojnaPermissions) {
        migrated.schemes = { ...defaultPrivs.schemes, ...oldPrivileges.yojnaPermissions };
        // Set tab visibility based on permissions
        if (oldPrivileges.yojnaPermissions.read || oldPrivileges.yojnaPermissions.write) {
            migrated.tabVisibility.schemes = true;
        }
    }
    
    return migrated;
};

const UserGroupsPage = () => {
    const [userGroups, setUserGroups] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [editingGroup, setEditingGroup] = useState(null);
    const { checkPermission, handlePermissionAction } = usePermissions();
    const refreshListTimeoutRef = useRef(null);
    const [formData, setFormData] = useState({
        groupName: '',
        privileges: {
            // Tab Visibility - Control which tabs are visible in the navigation
            tabVisibility: {
                home: true,
                appUsers: false,
                schemes: false,
                systemUsers: false,
                userGroups: false,
                masters: false,
                reports: false,
                auditLogs: false
            },
            // App Users Permissions
            appUsers: {
                create: false,
                import: false,
                export: false,
                read: false,
                write: false
            },
            // App User Details Visibility
            appUserDetails: {
                username: false,
                email: false,
                phoneNo: false,
                dob: false
            },
            // Schemes (Yojana) Permissions
            schemes: {
                import: false,
                export: false,
                read: false,
                write: false
            },
            // System Users Permissions
            systemUsers: {
                create: false,
                read: false,
                write: false
            },
            // Masters Permissions
            masters: {
                create: false,
                read: false,
                write: false
            },
            // Reports Permissions
            reports: {
                read: false,
                export: false
            }
        }
    });

    // Fetch user groups on component mount
    useEffect(() => {
        let cancelled = false;
        fetchUserGroupsData(() => cancelled);
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(
        () => () => {
            if (refreshListTimeoutRef.current != null) {
                clearTimeout(refreshListTimeoutRef.current);
                refreshListTimeoutRef.current = null;
            }
        },
        []
    );

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const fetchUserGroupsData = async (isStale = () => false) => {
        setLoading(true);
        try {
            const response = await fetchUserGroups();
            if (isStale()) return;
            console.log('User groups response:', response);

            if (!response || !response.data) {
                console.warn('No data received from API');
                setUserGroups([]);
                return;
            }

            const groups = Array.isArray(response.data) ? response.data : [];
            console.log('Processed user groups:', groups);

            setUserGroups(groups);
            setError(null);
        } catch (error) {
            console.error('Error fetching user groups:', error);
            if (!isStale()) {
                setError('Failed to load user groups. Please try again.');
                setUserGroups([]);
            }
        } finally {
            if (!isStale()) setLoading(false);
        }
    };

    const handleClickOpen = (group = null) => {
        if (group) {
            handlePermissionAction(PERMISSIONS.USER, ACTIONS.WRITE, () => {
                setEditingGroup(group);
                let privileges;
                try {
                    const parsed = typeof group.privileges === 'string' 
                        ? JSON.parse(group.privileges) 
                        : (group.privileges || {});
                    
                    // Migrate old structure to new structure if needed
                    privileges = migratePrivilegesStructure(parsed);
                } catch (error) {
                    console.error('Error parsing privileges:', error);
                    privileges = getDefaultPrivileges();
                }
                setFormData({
                    groupName: group.groupName || '',
                    privileges
                });
                setOpen(true);
            });
        } else {
            handlePermissionAction(PERMISSIONS.USER, ACTIONS.CREATE, () => {
                setEditingGroup(null);
                setFormData({
                    groupName: '',
                    privileges: getDefaultPrivileges()
                });
                setOpen(true);
            });
        }
    };

    const handleClose = () => {
        setOpen(false);
        setEditingGroup(null);
        setFormData({
            groupName: '',
            privileges: getDefaultPrivileges()
        });
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePermissionChange = (category, permission) => {
        setFormData(prev => {
            // Ensure the category exists with defaults
            const defaultPrivs = getDefaultPrivileges();
            const currentCategory = prev.privileges[category] || defaultPrivs[category] || {};
            
            const newPrivileges = {
                ...prev.privileges,
                [category]: {
                    ...currentCategory,
                    [permission]: !currentCategory[permission]
                }
            };
            
            // Auto-enable tab visibility when permissions are granted
            if (category !== 'tabVisibility' && category !== 'appUserDetails') {
                const tabMap = {
                    'appUsers': 'appUsers',
                    'schemes': 'schemes',
                    'systemUsers': 'systemUsers',
                    'masters': 'masters',
                    'reports': 'reports'
                };
                
                const tabKey = tabMap[category];
                if (tabKey && (permission === 'read' || permission === 'write')) {
                    // If read or write is enabled, show the tab
                    if (newPrivileges[category][permission]) {
                        newPrivileges.tabVisibility = {
                            ...(newPrivileges.tabVisibility || defaultPrivs.tabVisibility),
                            [tabKey]: true
                        };
                    }
                }
            }
            
            // Ensure tabVisibility always exists
            if (!newPrivileges.tabVisibility) {
                newPrivileges.tabVisibility = defaultPrivs.tabVisibility;
            }
            
            console.log('Permission changed:', { category, permission, newPrivileges });
            
            return {
                ...prev,
                privileges: newPrivileges
            };
        });
    };

    const handleSubmit = async () => {
        handlePermissionAction(PERMISSIONS.USER, editingGroup ? ACTIONS.WRITE : ACTIONS.CREATE, async () => {
            try {
                // Ensure all privilege fields are properly set with defaults
                const privilegesToSave = {
                    tabVisibility: { ...getDefaultPrivileges().tabVisibility, ...formData.privileges.tabVisibility },
                    appUsers: { ...getDefaultPrivileges().appUsers, ...formData.privileges.appUsers },
                    appUserDetails: { ...getDefaultPrivileges().appUserDetails, ...formData.privileges.appUserDetails },
                    schemes: { ...getDefaultPrivileges().schemes, ...formData.privileges.schemes },
                    systemUsers: { ...getDefaultPrivileges().systemUsers, ...formData.privileges.systemUsers },
                    masters: { ...getDefaultPrivileges().masters, ...formData.privileges.masters },
                    reports: { ...getDefaultPrivileges().reports, ...formData.privileges.reports }
                };

                const submitData = {
                    groupName: formData.groupName,
                    privileges: JSON.stringify(privilegesToSave)
                };

                console.log('Submitting user group data:', submitData);
                console.log('Privileges being saved:', privilegesToSave);
                console.log('Editing group:', editingGroup);

                if (editingGroup) {
                    const response = await updateUserGroup(editingGroup.userGroupId, submitData, {
                        previousData: editingGroup,
                        identifier: editingGroup.groupName || submitData.groupName
                    });
                    console.log('Update response:', response);
                    setSuccessMessage('User group updated successfully!');
                } else {
                    const response = await addUserGroup(submitData, {
                        identifier: submitData.groupName
                    });
                    console.log('Create response:', response);
                    setSuccessMessage('User group added successfully!');
                }
                
                // Refresh the list after a short delay to ensure backend has processed
                if (refreshListTimeoutRef.current != null) clearTimeout(refreshListTimeoutRef.current);
                refreshListTimeoutRef.current = setTimeout(() => {
                    refreshListTimeoutRef.current = null;
                    fetchUserGroupsData();
                }, 500);
                handleClose();
            } catch (error) {
                console.error('Error saving user group:', error);
                console.error('Error details:', error.response?.data);
                setError('Failed to save user group. Please try again.');
            }
        });
    };

    const handleDelete = async (group) => {
        handlePermissionAction(PERMISSIONS.USER, ACTIONS.WRITE, async () => {
            if (window.confirm('Are you sure you want to delete this user group?')) {
                try {
                    await deleteUserGroup(group.userGroupId || group.id, {
                        previousData: group,
                        identifier: group.groupName
                    });
                    setSuccessMessage('User group deleted successfully!');
                    fetchUserGroupsData();
                } catch (error) {
                    console.error('Error deleting user group:', error);
                    setError('Failed to delete user group. Please try again.');
                }
            }
        });
    };

    const renderPermissions = (group) => {
        if (!group) return null;
        
        let privileges;
        try {
            const parsed = typeof group.privileges === 'string' 
                ? JSON.parse(group.privileges) 
                : (group.privileges || {});
            privileges = migratePrivilegesStructure(parsed);
        } catch (error) {
            console.error('Error parsing privileges:', error);
            return (
                <Box>
                    <Typography variant="body2" color="error">
                        Error loading permissions
                    </Typography>
                </Box>
            );
        }

        const visibleTabs = privileges.tabVisibility 
            ? Object.entries(privileges.tabVisibility)
                .filter(([_, visible]) => visible)
                .map(([tab]) => tab)
            : [];

        return (
            <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Visible Tabs:</strong> {visibleTabs.length > 0 ? visibleTabs.join(', ') : 'None'}
                </Typography>
                {privileges.appUsers?.read && (
                    <Typography variant="body2" color="text.secondary">
                        App Users: {Object.entries(privileges.appUsers).filter(([_, v]) => v).map(([k]) => k).join(', ')}
                    </Typography>
                )}
                {privileges.schemes?.read && (
                    <Typography variant="body2" color="text.secondary">
                        Schemes: {Object.entries(privileges.schemes).filter(([_, v]) => v).map(([k]) => k).join(', ')}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Container maxWidth="lg">
            <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4">User Groups</Typography>
                    {checkPermission(PERMISSIONS.USER, ACTIONS.CREATE) && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => handleClickOpen()}
                            type="button"
                        >
                            Add New Group
                        </Button>
                    )}
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : userGroups.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                        <Typography color="textSecondary">
                            No user groups found. {checkPermission(PERMISSIONS.USER, ACTIONS.CREATE) ? 'Create a new group to get started.' : ''}
                        </Typography>
                    </Box>
                ) : (
                    <List>
                        {userGroups.map((group) => (
                            <ListItem 
                                key={group.userGroupId || group.id}
                                divider
                                sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    py: 2 
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1" component="div">
                                            {group.groupName}
                                        </Typography>
                                    }
                                    secondary={renderPermissions(group)}
                                    sx={{ mr: 2 }}
                                />
                                {checkPermission(PERMISSIONS.USER, ACTIONS.WRITE) && (
                                    <ListItemSecondaryAction>
                                        <IconButton 
                                            edge="end" 
                                            onClick={() => handleClickOpen(group)}
                                            aria-label="edit"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton 
                                            edge="end" 
                                            onClick={() => handleDelete(group)}
                                            aria-label="delete"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                )}
                            </ListItem>
                        ))}
                    </List>
                )}

                <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {editingGroup ? 'Edit User Group' : 'Add New User Group'}
                    </DialogTitle>
                    <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Group Name"
                            name="groupName"
                            fullWidth
                            value={formData.groupName}
                            onChange={handleInputChange}
                            required
                        />
                        
                        <Divider sx={{ my: 2 }} />
                        
                        {/* Tab Visibility Section */}
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Tab Visibility</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Control which tabs are visible in the navigation menu
                                </Typography>
                                <FormGroup>
                                    {[
                                        { key: 'home', label: 'Home' },
                                        { key: 'appUsers', label: 'App Users' },
                                        { key: 'schemes', label: 'Schemes' },
                                        { key: 'systemUsers', label: 'System Users' },
                                        { key: 'userGroups', label: 'User Groups' },
                                        { key: 'masters', label: 'Masters' },
                                        { key: 'reports', label: 'Reports' },
                                        { key: 'auditLogs', label: 'Audit Logs' }
                                    ].map(({ key, label }) => (
                                        <FormControlLabel
                                            key={key}
                                            control={
                                                <Checkbox
                                                    checked={formData.privileges.tabVisibility[key] || false}
                                                    onChange={() => handlePermissionChange('tabVisibility', key)}
                                                />
                                            }
                                            label={label}
                                        />
                                    ))}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>

                        {/* App Users Permissions */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">App Users Permissions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    {['create', 'read', 'write', 'import', 'export'].map((action) => (
                                        <FormControlLabel
                                            key={action}
                                            control={
                                                <Checkbox
                                                    checked={formData.privileges.appUsers[action] || false}
                                                    onChange={() => handlePermissionChange('appUsers', action)}
                                                />
                                            }
                                            label={action.charAt(0).toUpperCase() + action.slice(1)}
                                        />
                                    ))}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>

                        {/* App User Details Visibility */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">App User Details Visibility</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    {[
                                        { key: 'username', label: 'Username' },
                                        { key: 'email', label: 'Email' },
                                        { key: 'phoneNo', label: 'Phone Number' },
                                        { key: 'dob', label: 'Date of Birth' }
                                    ].map(({ key, label }) => (
                                        <FormControlLabel
                                            key={key}
                                            control={
                                                <Checkbox
                                                    checked={formData.privileges.appUserDetails[key] || false}
                                                    onChange={() => handlePermissionChange('appUserDetails', key)}
                                                />
                                            }
                                            label={label}
                                        />
                                    ))}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>

                        {/* Schemes Permissions */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Schemes (Yojana) Permissions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    {['read', 'write', 'import', 'export'].map((action) => (
                                        <FormControlLabel
                                            key={action}
                                            control={
                                                <Checkbox
                                                    checked={formData.privileges.schemes[action] || false}
                                                    onChange={() => handlePermissionChange('schemes', action)}
                                                />
                                            }
                                            label={action.charAt(0).toUpperCase() + action.slice(1)}
                                        />
                                    ))}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>

                        {/* System Users Permissions */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">System Users Permissions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    {['create', 'read', 'write'].map((action) => (
                                        <FormControlLabel
                                            key={action}
                                            control={
                                                <Checkbox
                                                    checked={formData.privileges.systemUsers[action] || false}
                                                    onChange={() => handlePermissionChange('systemUsers', action)}
                                                />
                                            }
                                            label={action.charAt(0).toUpperCase() + action.slice(1)}
                                        />
                                    ))}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>

                        {/* Masters Permissions */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Masters Permissions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    {['create', 'read', 'write'].map((action) => (
                                        <FormControlLabel
                                            key={action}
                                            control={
                                                <Checkbox
                                                    checked={formData.privileges.masters[action] || false}
                                                    onChange={() => handlePermissionChange('masters', action)}
                                                />
                                            }
                                            label={action.charAt(0).toUpperCase() + action.slice(1)}
                                        />
                                    ))}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>

                        {/* Reports Permissions */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Reports Permissions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    {['read', 'export'].map((action) => (
                                        <FormControlLabel
                                            key={action}
                                            control={
                                                <Checkbox
                                                    checked={formData.privileges.reports[action] || false}
                                                    onChange={() => handlePermissionChange('reports', action)}
                                                />
                                            }
                                            label={action.charAt(0).toUpperCase() + action.slice(1)}
                                        />
                                    ))}
                                </FormGroup>
                            </AccordionDetails>
                        </Accordion>
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={handleClose}
                            type="button"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSubmit} 
                            variant="contained" 
                            color="primary"
                            type="button"
                        >
                            {editingGroup ? 'Update' : 'Add'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Container>
    );
};

export default UserGroupsPage;